<template>
  <div ref="fab" class="fab z-0 touch-none transition-transform" :style="containerStyle">
    <div class="relative h-full w-full" :style="frameStyle">
      <button
        class="group relative h-full w-full cursor-pointer transition-transform duration-200 ease-out hover:translate-x-0"
        :class="isDragging ? 'translate-x-0' : '-translate-x-1/2'"
        @pointerdown="handlePointerDown"
        @pointerup="handlePointerUp"
        @pointercancel="handlePointerCancel"
      >
        <svg
          v-if="!isDragging"
          viewBox="0 0 80 120"
          class="fill-primary pointer-events-none absolute inset-0 h-full w-full group-hover:fill-[color-mix(in_oklab,#01a7ff,#000_7%)]"
          aria-hidden="true"
        >
          <path
            d="M 0 0 v 20.524 78.819 20.657 l 39.925 -0.132 c 0 0 -0.022 -10.523 6.877 -13.386 l -0.003 -0.009 A 50.021 49.428 0 0 0 80 59.934 50.021 49.428 0 0 0 46.754 13.377 l 0.003 -0.008 c -6.899 -2.863 -6.831 -13.369 -6.831 -13.369 z"
          />
        </svg>
        <div
          v-else
          class="btn btn-primary btn-active pointer-events-none absolute rounded-full"
          :style="dragCircleStyle"
        />
        <FontAwesomeIcon
          :icon="faQrcode"
          class="text-primary-content absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          :class="props.fontSize"
        />
      </button>
    </div>
    <Teleport to="body">
      <svg
        width="0"
        height="0"
        class="pointer-events-none fixed overflow-hidden"
        aria-hidden="true"
      >
        <defs>
          <clipPath :id="clipId" clipPathUnits="userSpaceOnUse">
            <path
              :transform="clipPathTransform"
              d="M 0 0 V 120 c 0 -5.827 4.742 -10.518 10.632 -10.518 H 24.871 29.963 V 10.518 H 24.871 16.272 10.632 c -5.89 0 -10.632 -4.691 -10.632 -10.518 z M 29.979 10.518 V 109.482 A 50.021 49.482 0 0 0 80 60 50.021 49.482 0 0 0 29.979 10.518 Z"
            />
          </clipPath>
        </defs>
      </svg>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { faQrcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useDraggable, useWindowSize } from '@vueuse/core';
import {
  computed,
  nextTick,
  onMounted,
  ref,
  Teleport,
  useId,
  useTemplateRef,
  watch,
  type StyleValue,
} from 'vue';

const BASE_WIDTH = 80;
const BASE_HEIGHT = 120;
const DRAG_SIZE = 90;
const DRAG_THRESHOLD = 8;

const props = withDefaults(
  defineProps<{
    size?: number;
    fontSize?: string;
    initialSide?: 'left' | 'right';
    initialAnchor?: 'top' | 'bottom';
    initialOffset?: string;
  }>(),
  {
    size: 60,
    fontSize: 'text-3xl',
    initialSide: 'right',
    initialAnchor: 'bottom',
    initialOffset: '100px',
  },
);

const emit = defineEmits<{
  click: [event: PointerEvent];
}>();

const clipId = useId();

const fabRef = useTemplateRef('fab');
const { width, height } = useWindowSize();
const { x, y, isDragging: isPointerHeld } = useDraggable(fabRef, {
  onMove: (_position, event) => {
    if (!isPointerDown.value || !pointerStart.value) return;

    const dx = event.clientX - pointerStart.value.x;
    const dy = event.clientY - pointerStart.value.y;

    if (Math.hypot(dx, dy) > DRAG_THRESHOLD) didDragDuringPointerDown.value = true;
  },
});
const isPointerDown = ref(false);
const didDragDuringPointerDown = ref(false);
const pointerStart = ref<{ x: number; y: number; fabY: number }>();

const isDragging = computed(() => isPointerHeld.value && didDragDuringPointerDown.value);

const stickRight = computed(() => x.value >= width.value / 2);
const scale = computed(() => props.size / BASE_WIDTH);
const resolvedHeight = computed(() => props.size * (BASE_HEIGHT / BASE_WIDTH));
const clipPathTransform = computed(() => `scale(${scale.value})`);
const edgeTranslateX = computed(() => (stickRight.value ? width.value - props.size : 0));

const containerStyle = computed<StyleValue>(() => ({
  left: '0',
  right: 'unset',
  top: `${y.value}px`,
  bottom: 'unset',
  width: `${props.size}px`,
  height: `${resolvedHeight.value}px`,
  transform: `translateX(${edgeTranslateX.value}px)`,
}));

const frameStyle = computed<StyleValue>(() => ({
  clipPath: isDragging.value ? 'none' : `url(#${clipId})`,
  transform: stickRight.value ? 'scaleX(-1)' : 'none',
}));

const dragCircleStyle = computed<StyleValue>(() => ({
  left: `${((BASE_WIDTH - DRAG_SIZE) / 2) * scale.value}px`,
  top: `${((BASE_HEIGHT - DRAG_SIZE) / 2) * scale.value}px`,
  width: `${DRAG_SIZE * scale.value}px`,
  height: `${DRAG_SIZE * scale.value}px`,
}));

onMounted(async () => {
  await nextTick();

  x.value = props.initialSide === 'right' ? width.value - props.size : 0;

  const offset = resolveViewportOffset(props.initialOffset, props.initialAnchor);
  y.value =
    props.initialAnchor === 'bottom' ? height.value - resolvedHeight.value - offset : offset;
});

function handlePointerDown(event: PointerEvent) {
  isPointerDown.value = true;
  didDragDuringPointerDown.value = false;
  pointerStart.value = { x: event.clientX, y: event.clientY, fabY: y.value };
}

function handlePointerUp(event: PointerEvent) {
  if (isPointerDown.value && !didDragDuringPointerDown.value) {
    // undo the sub-threshold drift useDraggable already applied
    if (pointerStart.value) y.value = pointerStart.value.fabY;

    emit('click', event);
  }

  resetPointerState();
}

function handlePointerCancel() {
  resetPointerState();
}

function resetPointerState() {
  isPointerDown.value = false;
  didDragDuringPointerDown.value = false;
  pointerStart.value = undefined;
}

function resolveViewportOffset(offset: string, anchor: 'top' | 'bottom') {
  const probe = document.createElement('div');

  probe.style.position = 'fixed';
  probe.style.width = '0';
  probe.style.height = '0';
  probe.style.pointerEvents = 'none';
  probe.style.visibility = 'hidden';
  probe.style.left = '0';
  probe.style[anchor] = offset;

  document.body.appendChild(probe);

  const rect = probe.getBoundingClientRect();
  probe.remove();

  return anchor === 'bottom' ? window.innerHeight - rect.top : rect.top;
}
</script>
